import React, { useState, useMemo } from 'react';
import Modal from '../../ui/Modal';
import { formatDateKey, formatDateRange } from './calendarUtils';

// ============================================
// DUPLICATE WEEK MODAL - Select target weeks
// ============================================
const DuplicateWeekModal = ({ isOpen, onClose, currentWeekStart, onDuplicate, slotsCount, bookingsCount }) => {
  const [selectedWeeks, setSelectedWeeks] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState('slots_only'); // 'slots_only' or 'slots_and_bookings'

  // Generate next 12 weeks as options
  const futureWeeks = useMemo(() => {
    const weeks = [];
    for (let i = 1; i <= 12; i++) {
      const weekStart = new Date(currentWeekStart);
      weekStart.setDate(weekStart.getDate() + (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 5); // Saturday (Mon-Sat)

      weeks.push({
        start: weekStart,
        end: weekEnd,
        key: formatDateKey(weekStart),
        label: formatDateRange(weekStart, weekEnd)
      });
    }
    return weeks;
  }, [currentWeekStart]);

  const toggleWeek = (weekKey) => {
    const newSelected = new Set(selectedWeeks);
    if (newSelected.has(weekKey)) {
      newSelected.delete(weekKey);
    } else {
      newSelected.add(weekKey);
    }
    setSelectedWeeks(newSelected);
  };

  const handleDuplicate = async () => {
    if (selectedWeeks.size === 0) return;

    setIsLoading(true);
    try {
      await onDuplicate(Array.from(selectedWeeks), duplicateMode === 'slots_and_bookings');
      setSelectedWeeks(new Set());
      setDuplicateMode('slots_only');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const sourceWeekStr = formatDateRange(
    currentWeekStart,
    new Date(currentWeekStart.getTime() + 5 * 24 * 60 * 60 * 1000)
  );

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg">
      <Modal.Header onClose={onClose}>
        <h3 className="text-xl font-bold text-white">Duplicate Week</h3>
        <p className="text-sm text-slate-400 mt-1">
          Copy from <span className="text-blue-300">{sourceWeekStr}</span>
        </p>
      </Modal.Header>

      <Modal.Body>
        {/* Duplicate Mode Selection */}
        <div className="mb-4 p-4 bg-slate-700/30 rounded-lg">
          <p className="text-sm text-slate-300 font-medium mb-3">Duplicate mode:</p>
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-slate-600/30 transition">
              <input
                type="radio"
                name="duplicateMode"
                value="slots_only"
                checked={duplicateMode === 'slots_only'}
                onChange={(e) => setDuplicateMode(e.target.value)}
                className="mt-1 w-4 h-4 text-blue-500"
              />
              <div>
                <div className="text-white font-medium">Copy slots only</div>
                <div className="text-slate-400 text-xs">Duplicate the schedule structure but leave all slots empty</div>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-slate-600/30 transition">
              <input
                type="radio"
                name="duplicateMode"
                value="slots_and_bookings"
                checked={duplicateMode === 'slots_and_bookings'}
                onChange={(e) => setDuplicateMode(e.target.value)}
                className="mt-1 w-4 h-4 text-blue-500"
              />
              <div>
                <div className="text-white font-medium">Copy slots and bookings</div>
                <div className="text-slate-400 text-xs">Duplicate both slots and existing bookings (active clients only)</div>
              </div>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm text-slate-400 mb-3">Select destination weeks:</p>

          <div className="max-h-48 overflow-y-auto space-y-1">
            {futureWeeks.map(week => (
              <div
                key={week.key}
                onClick={() => toggleWeek(week.key)}
                className={`p-3 rounded cursor-pointer transition flex items-center gap-3 ${
                  selectedWeeks.has(week.key)
                    ? 'bg-blue-500/30 border border-blue-400'
                    : 'bg-slate-700/30 hover:bg-slate-600/30 border border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedWeeks.has(week.key)}
                  onChange={() => {}}
                  className="w-4 h-4 rounded"
                />
                <span className="text-white">{week.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary before confirmation */}
        {selectedWeeks.size > 0 && (
          <div className="mt-4 p-4 bg-blue-500/10 rounded-lg border border-blue-400/30">
            <p className="text-white font-medium mb-2">This action will duplicate:</p>
            <div className="text-blue-200 text-sm space-y-1">
              <p>📅 <span className="font-medium">{slotsCount || 0}</span> slots</p>
              {duplicateMode === 'slots_and_bookings' && (
                <p>👥 <span className="font-medium">{bookingsCount || 0}</span> bookings (active clients only)</p>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-blue-400/20">
              <p className="text-slate-400 text-xs">
                Destination: <span className="font-medium text-white">{selectedWeeks.size} week(s)</span>
              </p>
              <p className="text-slate-500 text-xs mt-1">
                ⚠️ Existing slots/bookings in target weeks will be skipped
              </p>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button onClick={() => { onClose(); setDuplicateMode('slots_only'); setSelectedWeeks(new Set()); }} className="btn-secondary" disabled={isLoading}>
          Cancel
        </button>
        <button
          onClick={handleDuplicate}
          className="btn-primary"
          disabled={isLoading || selectedWeeks.size === 0}
        >
          {isLoading ? 'Duplicating...' : 'Duplicate'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DuplicateWeekModal;
